// Copyright (c) 2020 WiseTime. All rights reserved.

import React from "react"
import { useDispatch, useSelector } from "react-redux"
import styled from "styled-components/macro"
import * as TimelineActions from "../actions"
import { TrashTimeRowsRequest } from "generated/wisetime/user/timeline/v4/timeline_pb"
import TagsDropdown from "features/tags/TagsDropdown"
import ActivityTypesDropdown from "features/activity-types/ActivityTypesDropdown"
import AddToGroupDropdown from "../shared/AddToGroupDropdown"
import { ROW_ACTIONS_CLASSNAME } from "features/shared/classNames"
import { ActivityTypeRef } from "generated/wisetime/user/type/activity_type_ref_pb"
import { TagRef } from "generated/wisetime/user/type/tag_ref_pb"
import TrashIconButton from "../shared/TrashIconButton"
import { buildTrashedTimeRowAppcue } from "../userCues"
import * as UserCuesActions from "../../user-cues/actions"
import * as UserCuesSlice from "../../user-cues/slice"

const Actions = styled.div<{ isActive: boolean, hasTimeGroupsForDate: boolean }>`
  display: flex;
  align-self: flex-start;
  padding-top: 2px;
  margin-left: 7px;

  @media (max-width: ${({ theme }): string => theme.screen.md}) {
    display: none;
  }

  @media (min-width: ${({ theme }): string => theme.screen.md}) {
    opacity: 0;
    padding: 0;
    margin-top: 1px;
    margin-bottom: 1px;
    margin-right: ${({ hasTimeGroupsForDate }): string => hasTimeGroupsForDate ? "-60px" : "-30px"};
    background-color: ${({ theme }): string => theme.colors.white};
    border: 1px solid ${({ theme }): string => theme.colors.seashell};
    border-radius: 4px;
  }
`

type Props = {
  timeRowId: string,
  tagsList: TagRef.AsObject[],
  activityTypesList: ActivityTypeRef.AsObject[],
  hasTimeGroupsForDate: boolean,
  isActive: boolean,
}

const TimeBlockRowActions = ({
  timeRowId,
  tagsList,
  activityTypesList,
  hasTimeGroupsForDate,
  isActive,
}: Props): React.ReactElement<Props> => {
  const dispatch = useDispatch()

  const shownUserCues = useSelector(UserCuesSlice.selectUserCues)

  const timeRowsIdsList = [timeRowId]

  const onTrash = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {

    const request = new TrashTimeRowsRequest().setTimeRowIdsList(timeRowsIdsList)
    dispatch(TimelineActions.trashTimeRows(request))
    const trashedTimeRowAppCue = buildTrashedTimeRowAppcue(shownUserCues)
    if (trashedTimeRowAppCue) {
      dispatch(UserCuesActions.createUserCue(trashedTimeRowAppCue))
    }
    // Should not trigger the row click event here
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <Actions isActive={isActive} hasTimeGroupsForDate={hasTimeGroupsForDate} className={ROW_ACTIONS_CLASSNAME}>
      <TagsDropdown
        timeRowsIds={timeRowsIdsList}
        addedItemIds={tagsList.map(tag => tag.id)}
        icon="add-tag"
        isTimeRow
      />
      <ActivityTypesDropdown
        timeRowsIds={timeRowsIdsList}
        addedItemIds={activityTypesList.map(tag => tag.id)}
        icon="add-activity-type"
        isTimeRow
      />
      {hasTimeGroupsForDate && (
        <AddToGroupDropdown timeRowId={timeRowId}/>
      )}
      <TrashIconButton
        onClick={onTrash}
      />
    </Actions>
  )
}

export default TimeBlockRowActions
